import { inject, ref } from 'vue';
import { ExportCsv_Status } from '@/graphql/codegen/generated/graphql';
import { useNotify } from '@/services/notify/notify';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useI18n } from 'vue-i18n';
import { ErrorCodes } from '@/const/errorCodes';
import { IKPlatformAuthentication } from '@/modules/platformAuthentication/platformAuthentication.injectionKeys';
import moment from 'moment';

// eslint-disable-next-line no-shadow
export enum ExportOptionsEnum {
  ExportView = 'Export View',
  ExportAll = 'Export All',
}

export type ExportOptions = 'Export View' | 'Export All' | null;

export const useExportCsvHelper = () => {
  const isExportButtonDisabled = ref(false);
  const { toastMessage } = useNotify();
  const platformAuthentication = inject(IKPlatformAuthentication);
  const { t } = useI18n();
  const TIMEOUT_VALUE = 90000;
  let timeoutHandler: any = null;

  const handleCSVFailedFlow = (errorType?: ErrorCodes | null) => {
    if (errorType === ErrorCodes.TOO_MANY_CSV_REQUESTS) {
      toastMessage(t('workloads.exportCSV.exportFailedTooManyRequests'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
    } else toastMessage(t('workloads.exportCSV.exportFailedMessage'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
  };

  async function downloadCSVFile(fileId: string, filename: string = 'csv.file') {
    const { hostname, port } = new URL(window.location.href);
    const url = `https://${hostname}:${port}/csv-export/download/?fileid=${fileId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${platformAuthentication?.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        handleCSVFailedFlow();
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();

      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      handleCSVFailedFlow();
    }
  }

  const handleDownloadCSVFileProcess = () => {
    isExportButtonDisabled.value = true;
    timeoutHandler = setTimeout(() => {
      isExportButtonDisabled.value = false;
      handleCSVFailedFlow();
    }, TIMEOUT_VALUE);
  };

  const handleDownloadCSVFileOnResult = (status: ExportCsv_Status, errorType?: ErrorCodes | null) => {
    clearTimeout(timeoutHandler);
    isExportButtonDisabled.value = false;
    if (status === ExportCsv_Status.Failure) {
      handleCSVFailedFlow(errorType);
    }
  };

  const handleCSVFileName = (prefix: string, startTimestamp: any, endTimestamp: any) => {
    const startDate = moment(startTimestamp);
    const endDate = moment(endTimestamp);

    const formattedStartDate = startDate.format('D MMM');
    const formattedEndDate = endDate.format('D MMM YYYY');

    return `${prefix}_${formattedStartDate} - ${formattedEndDate}`;
  };

  return {
    isExportButtonDisabled,
    downloadCSVFile,
    handleDownloadCSVFileProcess,
    handleDownloadCSVFileOnResult,
    handleCSVFailedFlow,
    handleCSVFileName,
  };
};
